import {
    AttachMoney,
    BusinessCenter,
    ExpandMoreRounded as ExpandMoreRoundedIcon,
    GroupWorkOutlined as GroupWorkOutlinedIcon,
    Person,
} from 'components/icons';
import { cn } from 'utils/cn';
import Chip from 'components/@extended/Chip';
import SegmentChip from 'components/@extended/SegmentChip.tsx';
import { TableRowCallForActionButton } from 'components/buttons/CallForActionButton.tsx';
import ListCreateButton from 'components/buttons/ListCreateButton';
import Loader from 'components/Loader';
import RelationshipPartnersAndManagers from 'components/misc/RelationshipPartnersAndManagers.tsx';
import PartyName from 'components/party/PartyName';
import SubRowTable from 'components/tables/SubRowTable';
import { Separator } from 'components/ui/separator';
import { Tooltip } from 'components/ui/tooltip';
import type { CellContext } from '@tanstack/react-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import ColumnDefNestedTable from 'components/tables/list-table/ColumnDefNestedTable';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil.ts';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import useAuth from 'hooks/useAuth.ts';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { calculateTimeSince, TimeSinceCell } from 'pages/contacts/util';
import { getLinks } from 'pages/matters/util';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts.ts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftRowMenu } from 'components/drafts/DraftRecordActions';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import { useLazyGetGroupsWithFiltersQuery } from 'store/api/groups';
import { ContactListItem, PartyRelationshipTypeCode, PartyType, RelationshipTypeCode } from 'types/contacts';
import { GroupEmployeeRoleType, GroupMemberRoleType, PartyGroupListItem, PartyGroupParty } from 'types/groups';
import { NamedEntityMinDto } from 'types/other';
import { getClassifierText, t } from 'utils/getTranslations';
import { useLastEffortType } from 'hooks/useLastEffortType';
import { PartyNameParty } from 'utils/partyNameUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import GroupIcon from './GroupIcon';

const getGroupHref = (group: PartyGroupListItem) => `/group/${group.id}`;

const GroupsTable = ({
    contactId,
    passiveFilter = '',
}: {
    contactId?: string;
    passiveFilter?: string;
}): JSX.Element => {
    const navigateWithReturn = useNavigateWithReturnState();
    const isNewDesign = useIsNewDesign();

    const [fetchGroups, { data, isFetching }] = useLazyGetGroupsWithFiltersQuery();
    const fetchData = useWithDrafts(fetchGroups);
    const { user } = useAuth();
    const userId = user?.id;
    const lastEffortType = useLastEffortType();
    const hasLastEffortType = !!lastEffortType;
    const lastEffortTypeLabel = lastEffortType?.label ?? '';
    const dateFormat = useEffectiveDateFormat();

    // Build default filters for groups (my groups by default if userId available)
    const initialSearchParams = userId ? `qf=employees.employee.id==${userId}` : '';

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: [
            'GROUP_PARTNERSHIP_TYPE',
            'BUSINESS_AREA_TYPE',
            'RELATIONSHIP_TYPE',
            'SERVICE_LEVEL_TYPE',
            'SEGMENT_TYPE',
            'GROUP_TYPE',
        ],
    });

    // Weights measured at 2560/1680/1366 — width-stable there, so the ratios hold at every
    // resolution.
    const columns = useMemo<DataTableColumnDef<PartyGroupListItem>[]>(
        () => [
            {
                header: t('groups.group'),
                id: 'groupName',
                accessorKey: 'groupName',
                meta: {
                    widthWeight: 1.55,
                    minPx: 240,
                },
                cell: ({ row }) => {
                    const groupListItem = row.original;
                    const link = (
                        <RouterLink
                            className="entity-link text-fg hover:text-link w-fit font-semibold no-underline hover:underline"
                            to={`/group/${groupListItem.id}`}
                        >
                            {groupListItem.groupName || (groupListItem.isDraft ? t('new-entity.group') : '-')}
                            <GroupIcon size="small" className="ml-1" />
                        </RouterLink>
                    );
                    return (
                        <div className="flex flex-col gap-1">
                            {/* The list has no actions column, so a draft's menu sits by its name. */}
                            {groupListItem.isDraft ? (
                                <div className="flex w-full items-start justify-between gap-1">
                                    {link}
                                    <DraftRowMenu draft={{ type: 'group', id: groupListItem.id }} className="size-7" />
                                </div>
                            ) : (
                                link
                            )}
                            <div className="flex gap-1">
                                {groupListItem.isDraft ? <DraftChip /> : null}
                                {(groupListItem.segmentTypeCode != null ||
                                    groupListItem.serviceLevelTypeCode != null) && (
                                    <>
                                        <div className="flex gap-1">
                                            {groupListItem.segmentTypeCode != null && (
                                                <SegmentChip segmentTypeCode={groupListItem.segmentTypeCode} />
                                            )}
                                            {groupListItem.serviceLevelTypeCode != null && (
                                                <span className="text-subtitle2 text-grey-600 flex items-center font-semibold">
                                                    {getClassifierText(
                                                        classifiers['SERVICE_LEVEL_TYPE'],
                                                        groupListItem.serviceLevelTypeCode
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                        <Separator orientation="vertical" />
                                    </>
                                )}
                                <span className="text-caption text-fg-secondary flex items-center">
                                    {getClassifierText(
                                        classifiers['GROUP_PARTNERSHIP_TYPE'],
                                        groupListItem.groupPartnershipTypeCode
                                    )}
                                </span>
                            </div>
                        </div>
                    );
                },
            },
            {
                header: t('contacts.last-bd-effort'),
                accessorKey: 'lastEffortDate',
                // Not sortable: effort dates are transient values fetched per page, not a DB column.
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ getValue }) => (
                    <p className="text-body1">{calculateTimeSince(getValue(), null, dateFormat).text}</p>
                ),
            },
            ...(hasLastEffortType
                ? [
                      {
                          header: lastEffortTypeLabel,
                          id: 'lastHCSBdDate',
                          // The accessor is what makes the header sort: v8 gates getCanSort on one, manualSorting
                          // included. The id stays the backend's sort key.
                          accessorKey: 'lastHCSEffortDate',
                          meta: {
                              widthWeight: 1.25,
                              minPx: 130,
                              nullHandling: 'nullsLast' as const,
                          },
                          cell: ({ row }: CellContext<PartyGroupListItem, unknown>) => (
                              <p className="text-body1">
                                  {calculateTimeSince(row.original.lastHCSEffortDate, null, dateFormat).text}
                              </p>
                          ),
                      },
                  ]
                : []),
            {
                header: t('groups.primary-contact-persons'),
                accessorKey: 'parties',
                enableSorting: false,
                meta: {
                    widthWeight: 1.25,
                    minPx: 150,
                },
                cell: ({ getValue }) => {
                    const parties = (getValue<PartyGroupListItem['parties']>() || [])
                        .filter((item) => item.groupMemberRoleTypeCode === GroupMemberRoleType.PRIMARY_CONTACT)
                        .map((item) => item.party);
                    return parties.length ? getLinks('contact', parties) : null;
                },
            },
            {
                header: t('groups.relationship-owners'),
                accessorKey: 'employees',
                enableSorting: false,
                meta: {
                    widthWeight: 1.28,
                    minPx: 150,
                },
                cell: ({ getValue }) => {
                    const value = getValue<PartyGroupListItem['employees']>();
                    const relationshipPartners = (value || [])
                        .filter((item) => item.groupEmployeeRoleTypeCode === GroupEmployeeRoleType.RELATIONSHIP_PARTNER)
                        .map((item) => item.employee);
                    const relationshipManagers = (value || [])
                        .filter((item) => item.groupEmployeeRoleTypeCode === GroupEmployeeRoleType.RELATIONSHIP_MANAGER)
                        .map((item) => item.employee);

                    return (
                        <RelationshipPartnersAndManagers
                            relationshipManagers={relationshipManagers}
                            relationshipPartners={relationshipPartners}
                        />
                    );
                },
            },
            {
                id: 'expander',
                enableSorting: false,
                meta: {
                    // 32px toggle button + the managed cell's 32px horizontal padding.
                    fixedPx: 64,
                },
                cell: ({ row }) => {
                    const { parties } = row.original;
                    const hasMembers = parties?.some(
                        (item: PartyGroupParty) => item.groupMemberRoleTypeCode === GroupMemberRoleType.MEMBER
                    );
                    return hasMembers ? (
                        <TableRowCallForActionButton
                            onClick={row.getToggleExpandedHandler()}
                            className="cursor-pointer"
                            aria-label={t(row.getIsExpanded() ? 'collapse-row' : 'expand-row')}
                            shape="rounded"
                        >
                            <ExpandMoreRoundedIcon
                                className={cn(
                                    'new:motion-safe:transition-transform new:duration-(--duration-fast) new:ease-(--ease-smooth-out)',
                                    row.getIsExpanded() && '-scale-y-100'
                                )}
                            />
                        </TableRowCallForActionButton>
                    ) : null;
                },
            },
        ],
        [hasLastEffortType, lastEffortTypeLabel, classifiers, dateFormat]
    );

    const innerColumns = useMemo<DataTableColumnDef<ContactListItem>[]>(
        () => [
            {
                header: () => (
                    <div className="ml-8">
                        <FormattedMessage id="name" />
                    </div>
                ),
                accessorKey: 'name',
                enableSorting: false,
                cell: ({ cell }) => {
                    const { partyType, relationshipTypeCode } = cell.row.original;
                    return (
                        <div className="ml-8 flex flex-col gap-1">
                            <PartyName
                                party={cell.row.original as PartyNameParty}
                                isLink
                                plainLink
                                variant="list"
                                className="font-semibold"
                            />
                            <div className="flex gap-1">
                                {partyType === PartyType.COMPANY ? (
                                    <Tooltip content={t('contacts.types.company')}>
                                        <div>
                                            <Chip size="small" label={<BusinessCenter />} type={'partyType'} />
                                        </div>
                                    </Tooltip>
                                ) : (
                                    <Tooltip content={t('contacts.types.person')}>
                                        <div>
                                            <Chip size="small" label={<Person />} type={'partyType'} />
                                        </div>
                                    </Tooltip>
                                )}
                                {relationshipTypeCode === RelationshipTypeCode.CLIENT ? (
                                    <Tooltip
                                        content={getClassifierText(
                                            classifiers['RELATIONSHIP_TYPE'],
                                            RelationshipTypeCode.CLIENT
                                        )}
                                    >
                                        <div>
                                            <Chip size="small" label={<AttachMoney />} type={'partyType'} />
                                        </div>
                                    </Tooltip>
                                ) : null}
                            </div>
                        </div>
                    );
                },
            },
            {
                header: t('contacts.last-bd-effort'),
                accessorKey: 'lastBdEffort',
                enableSorting: false,
                cell: ({ cell }) => {
                    const { text: timeSinceLastEffort, shouldHighlight } = calculateTimeSince(
                        cell.row.original?.lastBdEffort,
                        cell.row.original?.relationshipTypeCode,
                        dateFormat
                    );
                    return <TimeSinceCell text={timeSinceLastEffort} overdue={shouldHighlight} />;
                },
            },
            {
                header: t('groups.primary-contact-persons'),
                id: 'relations',
                enableSorting: false,
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-col">
                            {(row.original?.relations || [])
                                ?.filter(
                                    (item) =>
                                        item.partyRelationshipTypeCode === PartyRelationshipTypeCode.PRIMARY_CONTACT
                                )
                                .map((item) => (
                                    <div key={`primary-contact-persons-${item.otherParty.id}`}>
                                        <PartyName
                                            party={item.otherParty}
                                            isLink
                                            variant="list"
                                            className="whitespace-normal"
                                        />
                                    </div>
                                )) ?? ''}
                        </div>
                    );
                },
            },
            {
                header: t('groups.relationship-owners'),
                id: 'relationshipOwners',
                enableSorting: false,
                cell: ({ row }) => {
                    const { managers, partners } = row.original;

                    const relationshipPartners = partners
                        .filter((item) => item.partyEmployeeTypeCode === GroupEmployeeRoleType.RELATIONSHIP_PARTNER)
                        .map((item) => item.employee) as NamedEntityMinDto[];

                    const relationshipManagers = managers
                        .filter((item) => item.partyEmployeeTypeCode === GroupEmployeeRoleType.RELATIONSHIP_MANAGER)
                        .map((item) => item.employee) as NamedEntityMinDto[];
                    return (
                        <RelationshipPartnersAndManagers
                            relationshipManagers={relationshipManagers}
                            relationshipPartners={relationshipPartners}
                        />
                    );
                },
            },
            {
                id: 'menu',
                enableSorting: false,
                meta: {
                    width: '5%',
                },
                cell: () => {
                    return null;
                },
            },
        ],
        [classifiers, dateFormat]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                {
                    selector: 'employees.employee.id',
                    filters: [
                        {
                            label: t('groups.my-groups'),
                            argument: userId?.toString() ?? '',
                        },
                    ],
                },
            ],
            filters: [
                {
                    type: 'text',
                    label: t('groups.group-name'),
                    group: 'groups.group',
                    selector: 'groupName',
                    multiple: true,
                },
                {
                    type: 'dynamicmultiselect',
                    subType: 'contact',
                    label: t('groups.groups-containing-contacts'),
                    group: 'groups.group',
                    selector: 'parties.party.id',
                    tooltip: 'groups.tooltips.groups-containing-contacts',
                    paginated: {
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'groups.group',
                    label: t('groups.relationship-partner'),
                    selector: 'id',
                    comperator: '=hasrelationshippartner=',
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'groups.group',
                    label: t('groups.relationship-manager'),
                    selector: 'id',
                    comperator: '=hasrelationshipmanager=',
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'multiselect',
                    label: t('groups.operates-in-business-areas'),
                    selector: 'businessAreas.businessAreaTypeCode',
                    group: 'groups.group',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['BUSINESS_AREA_TYPE']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('groups.segment-type'),
                    selector: 'segmentTypeCode',
                    group: 'groups.group',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['SEGMENT_TYPE']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('groups.service-level-type'),
                    selector: 'serviceLevelTypeCode',
                    group: 'groups.group',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['SERVICE_LEVEL_TYPE']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('groups.group-type'),
                    selector: 'groupTypeCode',
                    group: 'groups.group',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['GROUP_TYPE']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('groups.partnership-type'),
                    selector: 'groupPartnershipTypeCode',
                    group: 'groups.group',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['GROUP_PARTNERSHIP_TYPE']) : [],
                },
            ],
            searchBarSelectors: [
                { selector: 'groupName' },
                { selector: 'groupPartnershipTypeCode' },
                { selector: 'employees.employee.fullName' },
                { selector: 'parties.party.name' },
            ],
            passiveFilter: passiveFilter,
        }),
        [passiveFilter, userId, classifiers]
    );

    // The trailing `menu` column draws nothing; the new design's sub-table leaves it out.
    const nestedColumns = useMemo(() => innerColumns.filter(({ id }) => id !== 'menu'), [innerColumns]);

    const renderExpandedContent = useCallback(
        (row: PartyGroupListItem): JSX.Element => {
            const members = (row.parties || [])
                .filter((item) => item.groupMemberRoleTypeCode === GroupMemberRoleType.MEMBER)
                .map((item) => item.party);
            return isNewDesign ? (
                <ColumnDefNestedTable<ContactListItem> columns={nestedColumns} data={members} />
            ) : (
                <SubRowTable<ContactListItem> columns={innerColumns} data={members} />
            );
        },
        [innerColumns, nestedColumns, isNewDesign]
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<PartyGroupListItem>
            getRowHref={getGroupHref}
            columnConfigId="groups"
            columns={columns}
            renderExpandedContent={renderExpandedContent}
            data={data}
            isLoading={isFetching}
            pluralTextId="groups.groups"
            singularTextId="groups.group"
            noEntriesId="groups.no-groups-found"
            noEntriesBodyId="groups.empty.body"
            noEntriesIcon={GroupWorkOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.PARTY_GROUPS']}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn(contactId ? `/contact/${contactId}/group/new` : '/group/new')}
                    labelId="groups.add-new-group"
                    newLabelId="new-entity.group"
                />
            }
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
            disableUrlState={false}
            extraHeaderComponent={
                contactId ? (
                    <ListCreateButton
                        permissions={['CREATE.PARTY_GROUPS']}
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => navigateWithReturn(`/contact/${contactId}/group/new`)}
                        labelId="groups.add-new-group"
                        newLabelId="new-entity.group"
                    />
                ) : null
            }
            initialSearchParams={initialSearchParams}
        />
    );
};

export default GroupsTable;
